import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "polish-the-draft",
  courseId: "writing-and-editing",
  order: 2,
  title: "Polish the draft",
  subtitle: "Enhance, proofread, find and replace",
  summary:
    "Three tools clean up a draft: Enhance Writing rewrites a rough selection, writing suggestions underline what you edit, and Find and Replace fixes a name everywhere.",
  learn: [
    "Rewrite a selection with Enhance Writing and compare it before accepting",
    "Use writing suggestions to accept, dismiss, or add a word to your dictionary",
    "Replace a misheard name everywhere, and sweep for leftover placeholders",
  ],
  sceneId: "polish-the-draft",
  tourId: "polish-the-draft",
  minutes: 5,
  releasedAt: "2025-06-01T00:00:00.000Z",
  body: [
    {
      type: "paragraph",
      text: "A draft is rarely done after the first read. A note you typed between calls reads like a note, a name was misheard, a word keeps getting flagged. Each tool below handles one of those.",
    },
    { type: "heading", text: "Enhance Writing" },
    {
      type: "paragraph",
      text: "**Enhance Writing** turns a rough sentence or paragraph into report prose that matches the rest of the draft. It works on a selection, not the whole report.",
    },
    {
      type: "steps",
      items: [
        "Select the text in the narrative. The floating menu appears.",
        "Click **Enhance Writing**.",
        "Read the Original and Enhanced versions side by side.",
        "Click **Accept Changes** to replace the selection, or **Discard** to keep the original.",
      ],
    },
    {
      type: "paragraph",
      text: "The rewrite uses your Report Preferences, so it follows the same style as the rest of your reports.",
    },
    {
      type: "callout",
      tone: "note",
      text: "Enhance Writing never adds facts. If the enhanced version seems to know something your note did not say, read it again, because it should only be rephrasing. Placeholders in the selection are preserved.",
    },
    {
      type: "bullets",
      items: [
        "A paragraph that exists but reads rough: **Enhance Writing**.",
        "New content you would rather say than type: **Transcribe Audio** with Enhanced Dictation.",
        "The whole report needs to change: **Regenerate Narrative**.",
      ],
    },
    { type: "heading", text: "Writing suggestions" },
    {
      type: "paragraph",
      text: "The proofreader checks paragraphs you have edited. About a second after you stop typing, it underlines spelling, grammar, and clarity issues in that paragraph, like in Gmail. It never underlines the AI's own draft, only text you changed.",
    },
    {
      type: "steps",
      items: [
        "In the editor toolbar, click **Writing suggestions: on/off** to turn it on.",
        "Edit a paragraph and pause. Underlines appear on anything it flags.",
        "Click an underline to see the suggested replacement.",
        "Accept the replacement, or dismiss it.",
        "For a name or term it keeps flagging, click **Add to dictionary**.",
      ],
    },
    {
      type: "paragraph",
      text: "The on/off setting is stored in this browser. **Add to dictionary** writes the word to **Common Spellings** in your Report Preferences, so future drafts and checks use it. See Report preferences in depth for that setting. If you type a correction yourself, the underline on that span disappears on its own, so you do not have to dismiss it.",
    },
    {
      type: "bullets",
      items: [
        "Protected text it leaves alone: quoted speech, case and plate numbers, ALL-CAPS tokens, URLs, your name, rank, and call sign, and anything in Common Spellings.",
        "Meaning: it will not drop first-person pronouns, and it will not merge or delete sentences.",
        "Volume: it caps itself at eight marks per paragraph.",
      ],
    },
    { type: "heading", text: "Find and Replace" },
    {
      type: "paragraph",
      text: "If the transcript misheard a name and it shows up in six places, Find and Replace fixes all six at once. It is also the last check before a report leaves the app.",
    },
    {
      type: "steps",
      items: [
        "Click **Find** in the editor toolbar and type the text. Matches highlight, and you can step through them.",
        "Click **Find and Replace** to add the replacement field.",
        "Click **Replace** for the current match, or **Replace All** for every match in the narrative.",
      ],
    },
    {
      type: "callout",
      tone: "warning",
      title: "Matches inside words",
      text: "Search is case-insensitive and matches inside words, so replacing \"Lee\" also changes \"Leeds\". Search for the full name when you can.",
    },
    {
      type: "paragraph",
      text: "If a name is wrong because the transcript misheard it, also add it to Common Spellings so the next draft gets it right. Before you export, search for \"[\" to land on every remaining placeholder, and fill each one.",
    },
  ],
  related: ["editor-essentials", "report-preferences-in-depth", "review-the-draft"],
};

export default lesson;
